// Abschluss der Meldung (Meldung an den Versicherer): Säen aus dem Schaden und
// Abbilden eines Patches auf die Spalten des Schadens — reine Funktionen, damit
// die Seite schlank bleibt. Die Werte sind keine Meldungsfelder (`meldung`),
// sondern eigene Spalten, die sofort per PUT gespeichert werden.
import type { DamagePatch, MappeDamage } from "../types";
import type { AbschlussWerte, Meldeweg } from "./AbschlussIntern";

export const MELDEWEGE: readonly Meldeweg[] = ["Telefon", "E-Mail", "Portal", "Post"];

/** Typwache für einen Meldeweg (Wert aus `RadioGroup` oder vom Server). */
export function istMeldeweg(v: unknown): v is Meldeweg {
  return (MELDEWEGE as readonly unknown[]).includes(v);
}

export const ABSCHLUSS_LEER: AbschlussWerte = {
  reportDate: null,
  meldeweg: null,
  externalDamageNbr: "",
  sachbearbeiterVr: null,
  reguliererVr: null,
  sachverstaendiger: null,
};

const verweisAus = (c: { id: number; displayName?: string } | null | undefined) =>
  c ? { id: c.id, label: c.displayName ?? "" } : null;

/** Abschluss-Werte aus den Spalten des geladenen Schadens. */
export function abschlussAusSchaden(damage: MappeDamage): AbschlussWerte {
  const meldeweg = istMeldeweg(damage.meldeweg) ? damage.meldeweg : null;
  return {
    reportDate: damage.reportDate || null,
    meldeweg,
    externalDamageNbr: damage.externalDamageNbr ?? "",
    sachbearbeiterVr: verweisAus(damage.sachbearbeiterVrContact),
    reguliererVr: verweisAus(damage.reguliererContact),
    sachverstaendiger: verweisAus(damage.sachverstaendigerContact),
  };
}

/** Teil-PUT für genau die Felder des Patches. */
export function abschlussPatchFuerServer(patch: Partial<AbschlussWerte>): DamagePatch {
  const aus: DamagePatch = {};
  if ("reportDate" in patch) aus.reportDate = patch.reportDate ?? null;
  if ("meldeweg" in patch) aus.meldeweg = patch.meldeweg ?? null;
  if ("externalDamageNbr" in patch) aus.externalDamageNbr = patch.externalDamageNbr ?? "";
  if ("sachbearbeiterVr" in patch) {
    aus.sachbearbeiterVrContact = patch.sachbearbeiterVr ? { id: patch.sachbearbeiterVr.id } : null;
  }
  if ("reguliererVr" in patch) aus.reguliererContact = patch.reguliererVr ? { id: patch.reguliererVr.id } : null;
  if ("sachverstaendiger" in patch) {
    aus.sachverstaendigerContact = patch.sachverstaendiger ? { id: patch.sachverstaendiger.id } : null;
  }
  return aus;
}

/** Heutiges Datum (lokale Zeit) als `JJJJ-MM-TT`. */
export function heuteIso(jetzt: Date = new Date()): string {
  const zwei = (n: number) => String(n).padStart(2, "0");
  return `${jetzt.getFullYear()}-${zwei(jetzt.getMonth() + 1)}-${zwei(jetzt.getDate())}`;
}

/**
 * Nur die Felder des Patches, die sich gegenüber dem Stand ändern — Kontakte
 * nach Id (ein nachgeladener Name ist keine Änderung), sonst nach Wert.
 */
export function abschlussGeaendert(stand: AbschlussWerte, patch: Partial<AbschlussWerte>): Partial<AbschlussWerte> {
  const aus: Partial<AbschlussWerte> = {};
  if ("reportDate" in patch && patch.reportDate !== stand.reportDate) aus.reportDate = patch.reportDate;
  if ("meldeweg" in patch && patch.meldeweg !== stand.meldeweg) aus.meldeweg = patch.meldeweg;
  if ("externalDamageNbr" in patch && patch.externalDamageNbr !== stand.externalDamageNbr) {
    aus.externalDamageNbr = patch.externalDamageNbr;
  }
  for (const feld of ["sachbearbeiterVr", "reguliererVr", "sachverstaendiger"] as const) {
    if (feld in patch && (patch[feld]?.id ?? null) !== (stand[feld]?.id ?? null)) aus[feld] = patch[feld];
  }
  return aus;
}
